import { apiClient } from './client';

/**
 * 외부 메일 계정 (2026-08-20, migration 107).
 *
 * **받기와 보내기는 다른 규약**이다 — SMTP 만 넣으면 보내기만 되고 받은 편지함은 비어 있다.
 * 받기는 IMAP(권장)과 POP3 둘 다 된다. 그래서 두 벌을 함께 다루고, 확인 결과도 따로 돌려받는다.
 * 비밀번호는 서버가 돌려주지 않는다(있는지 없는지만).
 */

export type RecvProtocol = 'imap' | 'pop3';

export interface MailAccount {
  id: string;
  email: string;
  displayName: string | null;
  /** 받기 규약 — IMAP(권장) 또는 POP3(사내 서버가 POP3 만 여는 곳이 있다). */
  recvProtocol: RecvProtocol;
  recvHost: string | null;
  recvPort: number | null;
  recvSecure: boolean;
  recvUser: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean;
  smtpUser: string | null;
  /**
   * 가져올 IMAP 폴더 경로 (migration 169). 빈 배열이면 받은편지함만 — 지금까지와 같다.
   * ★ 구 서버는 이 칸을 안 준다(undefined) → 화면은 빈 배열로 읽는다.
   */
  recvFolders?: string[];
  /** Axi 에서 읽은 것을 서버에도 읽음으로 (migration 170). 구 서버는 안 준다 → false 로 읽는다. */
  syncReadToServer?: boolean;
  hasRecvPass: boolean;
  hasSmtpPass: boolean;
  syncIntervalSec: number;
  isDefaultSender: boolean;
  lastCheckedAt: string | null;
  /** null = 설정이 없어 확인 대상이 아님 / true·false = 확인 결과. */
  lastRecvOk: boolean | null;
  lastSmtpOk: boolean | null;
  lastError: string | null;
  /**
   * 마지막 **가져오기** (migration 112). 위의 lastChecked* 는 *연결 확인* 결과라 뜻이 다르다 —
   * "연결은 되는데 한 통도 안 들어온다" 와 "가져오다 실패했다" 는 다른 상태다.
   * 구 서버는 안 준다.
   */
  lastSyncAt?: string | null;
  lastSyncCount?: number;
  lastSyncError?: string | null;
  /**
   * 가져온 메일을 담을 내 메일함 (migration 164). null = 받은편지함.
   * ★ 자동분류 규칙에 걸리면 **그 규칙의 메일함이 이긴다**(좁은 것이 이긴다).
   */
  targetFolderId?: string | null;
  /** 가져온 뒤 원본 서버에서 지울지 (migration 164). 기본은 **안 지운다**. */
  deleteOriginal?: boolean;
}

/** 한 회차의 결과 (migration 112). */
export interface MailSyncSummary {
  accountId: string;
  /** 사서함에 있는 통 수(UIDL 기준). */
  total: number;
  imported: number;
  skipped: number;
  failed: number;
  /** 남은 것이 더 있다 — 워커가 이어받는다. */
  hasMore: boolean;
  error: string | null;
}

export interface ProbeResult { ok: boolean; message: string }

export interface MailAccountInput {
  email?: string;
  recvProtocol?: RecvProtocol;
  displayName?: string | null;
  recvHost?: string | null;
  recvPort?: number | null;
  recvSecure?: boolean;
  recvUser?: string | null;
  recvPass?: string | null;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpSecure?: boolean;
  smtpUser?: string | null;
  smtpPass?: string | null;
  /**
   * 「받기 비밀번호와 같음」 (2026-09-07). 켜면 서버가 **받기 비밀번호**를 보내기 칸에 담는다.
   * ★ 고칠 때 받기 비밀번호를 다시 안 적어도 된다 — 서버가 저장된 것을 푼다(화면은 그 값이 없다).
   */
  smtpSamePass?: boolean;
  /** 가져올 IMAP 폴더 경로 (migration 169). 빈 배열이면 받은편지함만. */
  recvFolders?: string[] | null;
  syncReadToServer?: boolean;
  syncIntervalSec?: number;
  isDefaultSender?: boolean;
  targetFolderId?: string | null;
  deleteOriginal?: boolean;
}

export interface MailServerGuess {
  recvHost?: string;
  recvPort: number;
  recvSecure: boolean;
  smtpHost?: string;
  smtpPort: number;
  smtpSecure: boolean;
  note?: string;
}

const BASE = '/users/me/mail-accounts';

export async function listMailAccounts(): Promise<MailAccount[]> {
  const { data } = await apiClient.get<{ accounts: MailAccount[] }>(BASE);
  return data.accounts;
}

/** 주소만으로 서버 칸을 채워 준다 — 흔한 서비스는 손으로 안 넣어도 된다. */
export async function guessMailServers(email: string, protocol: RecvProtocol = 'imap'): Promise<MailServerGuess> {
  const { data } = await apiClient.get<{ guess: MailServerGuess }>(
    `${BASE}/guess`, { params: { email, protocol } },
  );
  return data.guess;
}

export async function createMailAccount(input: MailAccountInput) {
  const { data } = await apiClient.post<{ account: MailAccount; recv: ProbeResult | null; smtp: ProbeResult | null }>(
    BASE, input,
  );
  return data;
}

export async function patchMailAccount(id: string, input: MailAccountInput) {
  const { data } = await apiClient.patch<{ account: MailAccount; recv: ProbeResult | null; smtp: ProbeResult | null }>(
    `${BASE}/${id}`, input,
  );
  return data;
}

export async function testMailAccount(id: string) {
  const { data } = await apiClient.post<{ account: MailAccount; recv: ProbeResult | null; smtp: ProbeResult | null }>(
    `${BASE}/${id}/test`,
  );
  return data;
}

/**
 * 저장 없이 연결만 확인 (2026-11-02) — 수정 팝업의 「연결확인」 단추.
 * ★ 아무것도 저장하지 않는다. 비밀번호를 비워 두면 `accountId` 의 저장된 것으로 시험한다.
 */
export async function probeMailAccount(input: MailAccountInput & { accountId?: string }) {
  const { data } = await apiClient.post<{ recv: ProbeResult | null; smtp: ProbeResult | null }>(
    `${BASE}/probe`, input,
  );
  return data;
}

/**
 * 지금 가져오기 (migration 112) — 확인 주기를 기다리지 않고 한 회차를 즉시 돌린다.
 *
 * 서버가 20초 안에 끝낼 만큼만 받고, 남은 것은 `hasMore` 로 알린 뒤 워커가 이어받는다.
 * 옛 서버는 이 길을 몰라 404 를 낸다 — 부르는 쪽이 그 뜻을 사람 말로 바꿔 준다.
 */
export async function syncMailAccount(id: string) {
  const { data } = await apiClient.post<{ account: MailAccount; sync: MailSyncSummary }>(
    `${BASE}/${id}/sync`,
  );
  return data;
}

export async function deleteMailAccount(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`);
}

/**
 * 서버가 가진 폴더 목록 (migration 169, 2026-09-07).
 *
 * ★ 저장된 계정에만 물을 수 있다 — 서버에 실제로 붙어 보는 일이라 비밀번호가 있어야 한다.
 * ★ POP3 계정은 400(`NO_FOLDERS_IN_POP3`) — 규약에 폴더가 없다.
 */
export interface ServerMailFolder {
  path: string;
  name: string;
  specialUse: string | null;
  messages: number | null;
  unseen: number | null;
  selectable: boolean;
}

export async function listServerFolders(id: string): Promise<ServerMailFolder[]> {
  const { data } = await apiClient.get<{ folders: ServerMailFolder[] }>(
    `${BASE}/${id}/folders`,
  );
  return data.folders ?? [];
}
